import React from "react";
import { Listbox } from "@headlessui/react";
import { Check, ChevronsUpDown } from "lucide-react";
import {
  COUNTRY_CODE_OPTIONS,
  DEFAULT_COUNTRY_CODE,
  type CountryCodeOption,
} from "../../constants/countryCodes";

interface CountryCodeSelectProps {
  value: string;
  onChange: (nextCode: string) => void;
  disabled?: boolean;
  className?: string;
  options?: CountryCodeOption[];
  buttonClassName?: string;
  panelClassName?: string;
}

const CountryCodeSelect: React.FC<CountryCodeSelectProps> = ({
  value,
  onChange,
  disabled = false,
  className = "",
  options = COUNTRY_CODE_OPTIONS,
  buttonClassName = "",
  panelClassName = "",
}) => {
  const fallbackCode =
    options.find((option) => option.code === DEFAULT_COUNTRY_CODE)?.code ||
    options[0]?.code ||
    "";
  const selectedCode = value || fallbackCode;

  return (
    <div className={`relative ${className}`}>
      <Listbox value={selectedCode} onChange={onChange} disabled={disabled}>
        <Listbox.Button
          className={`relative w-full cursor-default rounded-lg border border-gray-300 bg-white px-3 py-2 text-left text-sm text-gray-900 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 ${buttonClassName}`}
        >
          <span className="block truncate pr-6">{selectedCode}</span>
          <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
            <ChevronsUpDown className="h-4 w-4 text-gray-500 dark:text-gray-400" />
          </span>
        </Listbox.Button>

        <Listbox.Options
          className={`absolute z-50 mt-1 max-h-64 w-max min-w-full overflow-auto rounded-lg border border-gray-200 bg-white py-1 text-sm shadow-lg focus:outline-none dark:border-gray-700 dark:bg-gray-800 ${panelClassName}`}
        >
          {options.map((option) => (
            <Listbox.Option
              key={`${option.code}-${option.country}`}
              value={option.code}
              className={({ active }) =>
                `relative cursor-pointer select-none py-2 pl-9 pr-3 ${
                  active
                    ? "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300"
                    : "text-gray-900 dark:text-gray-100"
                }`
              }
            >
              {({ selected }) => (
                <>
                  <span className="block whitespace-nowrap">
                    {option.code} {option.country}
                  </span>
                  {selected ? (
                    <span className="absolute inset-y-0 left-2 flex items-center text-red-600 dark:text-red-300">
                      <Check className="h-4 w-4" />
                    </span>
                  ) : null}
                </>
              )}
            </Listbox.Option>
          ))}
        </Listbox.Options>
      </Listbox>
    </div>
  );
};

export default CountryCodeSelect;
